import { MouseEvent, useCallback, useEffect, useState } from 'react';
import { ProductFamilyName, marketplaceAddonDetails } from '../../../models';
import { useGlobalStore } from '../../../store';
import {
  monthlyPriceStyle,
  removeButtonStyle,
  userPriceStyle
} from '../../product-family-container/pricing.styles';
import { productTreeContainer } from '../../product-family-container/product-family-container.styles';
import {
  appWrapperContainer,
  appContainer,
  appLogoNameContainer,
  appIconStyle,
  appNameStyle,
  appPricingContainer
} from './app-card.styles';
import { appLogo } from './app-logo';
import { usePricingData } from '../../../hooks';
import { useHighestUnitCount } from '../../../selectors';
import {
  ANALYTICS_EVENTS,
  APPS_LIMIT_BY_PRODUCT_FAMILY,
  DEFAULT_MAX_UNIT_LIMIT
} from '../../../constants';
import { AsyncResult } from '@atlassiansox/bxp-react-core';
import {
  WithAnalyticsEventsProps,
  withAnalyticsEvents,
  useAnalyticsEvents
} from '@atlaskit/analytics-next';
import { ButtonProps } from '@atlassiansox/wpl-utils';
import { useModuleProps } from '../../../module-props';
import { GetUserTypeLabel } from '../../product-family-container/messages';

interface AppCardProps {
  app: marketplaceAddonDetails;
  productFamilyName: ProductFamilyName;
}

const RemoveButtonBase = ({
  createAnalyticsEvent,
  onClick,
  text,
  appName
}: WithAnalyticsEventsProps & ButtonProps & { appName: string }) => {
  const handleClick = useCallback(
    async (e: MouseEvent<HTMLButtonElement>) => {
      const analyticsEvent = createAnalyticsEvent?.({
        action: 'click',
        component: 'button',
        eventName: ANALYTICS_EVENTS.removeApp,
        removedApp: appName
      });

      analyticsEvent?.fire();

      if (onClick) {
        onClick(e);
      }
    },
    [onClick, createAnalyticsEvent]
  );

  return (
    <button css={removeButtonStyle} onClick={handleClick}>
      {text}
    </button>
  );
};

const RemoveButton = withAnalyticsEvents()(RemoveButtonBase);

const AppCard = ({ app, productFamilyName }: AppCardProps) => {
  const [
    {
      selectedBillingCycle,
      selectedAppsWithDetails,
      selectedMarketplaceAddons
    },
    {
      updateMarketplaceApp,
      incrementTotal,
      updateSelectedAppsWithDetails,
      appendAppError
    }
  ] = useGlobalStore();

  const { createAnalyticsEvent } = useAnalyticsEvents();

  const [highestUnitCount] = useHighestUnitCount(productFamilyName);

  const [currentTotalPrice, setCurrentTotalPrice] = useState(0);
  const [formattedTotal, setFormattedTotal] = useState('$0');
  const [perUnit, setPerUnit] = useState('$0');
  const {
    monthlyBillingCycleLabel,
    annuallyBillingCycleLabel,
    perUserTypeLabel,
    userTierLabel,
    appLabel,
    removeLabel,
    freeLabel
  } = useModuleProps();

  const fetchPriceCalc = usePricingData(
    app.offeringSku,
    '',
    highestUnitCount,
    'Standard',
    [
      1,
      APPS_LIMIT_BY_PRODUCT_FAMILY[productFamilyName] || DEFAULT_MAX_UNIT_LIMIT
    ]
  );

  useEffect(() => {
    const appsWithDetails = new Map();
    let totalAddOns: marketplaceAddonDetails[] = [];
    Array.from(selectedMarketplaceAddons.values()).forEach((app) => {
      const addon = [...app];
      totalAddOns = [...totalAddOns, ...addon];
    });

    totalAddOns.forEach((app) => {
      if (selectedAppsWithDetails.has(app.name)) {
        appsWithDetails.set(app.name, selectedAppsWithDetails.get(app.name));
      } else {
        appsWithDetails.set(app.name, {
          name: app?.name,
          selectedUserCount: 0,
          costPerUserType: '',
          monthlyCost: '',
          userType: ''
        });
      }
    });

    updateSelectedAppsWithDetails(appsWithDetails);
  }, [selectedMarketplaceAddons.size]);

  let unitLimitLower = '';
  let unitLimitUpper = '';

  const [toBeRemoved, setToBeRemoved] = useState(false);
  const remove = () => {
    setToBeRemoved(true);
    incrementTotal(productFamilyName, -currentTotalPrice);
    updateMarketplaceApp(productFamilyName, app);
  };

  if (AsyncResult.isComplete(fetchPriceCalc)) {
    const priceCalc = fetchPriceCalc.resolve()[0];

    if (priceCalc) {
      const {
        totalPrice,
        formattedTotalPrice,
        formattedAvgPerUnitPrice,
        formattedBreakdown
      } = priceCalc;

      const fmtBreakdownTier =
        formattedBreakdown[formattedBreakdown.length - 1];

      if (!fmtBreakdownTier) {
        throw new Error('No breakdown provided for user tier.');
      }

      const { formattedUnitLimitLower, formattedUnitLimitUpper } =
        fmtBreakdownTier;

      unitLimitLower = formattedUnitLimitLower;
      unitLimitUpper = formattedUnitLimitUpper;

      if (totalPrice !== currentTotalPrice) {
        incrementTotal(productFamilyName, totalPrice - currentTotalPrice);

        setCurrentTotalPrice(totalPrice);
        setFormattedTotal(formattedTotalPrice);
        setPerUnit(formattedAvgPerUnitPrice);

        const currentApps = new Map(selectedAppsWithDetails);
        const currentApp = currentApps.get(app.name);

        if (currentApp) {
          const changedApp = {
            ...currentApp,
            selectedUserCount: highestUnitCount,
            costPerUserType: formattedAvgPerUnitPrice,
            monthlyCost: formattedTotalPrice,
            userType: GetUserTypeLabel('USER'),
            userTypeTier: `${unitLimitLower} - ${unitLimitUpper}`
          };
          currentApps.set(app.name, changedApp);
        }

        updateSelectedAppsWithDetails(currentApps);
      }
    }
  } else if (AsyncResult.isError(fetchPriceCalc)) {
    if (!toBeRemoved) {
      const errorMessage = (fetchPriceCalc.error as Error).message;
      appendAppError(app.name, errorMessage);
      createAnalyticsEvent({
        component: 'marketplace_app',
        action: 'errored',
        eventName: ANALYTICS_EVENTS.appErrored,
        appName: app.name,
        offeringSku: app.offeringSku,
        errorMessage
      }).fire();
      remove();
    }
  }

  if (toBeRemoved) {
    return null;
  }

  return (
    <div css={productTreeContainer} data-testid="marketplace-app-card">
      <div css={appWrapperContainer}>
        <div css={appContainer}>
          <div css={appLogoNameContainer}>
            <div css={appIconStyle}>{appLogo}</div>
            <div css={appNameStyle}>
              {appLabel}: {app.name}
            </div>
          </div>
        </div>
        <div css={appPricingContainer}>
          {highestUnitCount !== 0 &&
            formattedTotal === '$0' &&
            highestUnitCount <
              (APPS_LIMIT_BY_PRODUCT_FAMILY[productFamilyName] ||
                DEFAULT_MAX_UNIT_LIMIT) && (
              <div>
                <div css={monthlyPriceStyle}>{freeLabel}</div>
              </div>
            )}
          {highestUnitCount !== 0 &&
            formattedTotal !== '$0' &&
            (selectedBillingCycle === 'MONTHLY' ? (
              <div>
                <div css={monthlyPriceStyle}>
                  {formattedTotal} {monthlyBillingCycleLabel.toLowerCase()}
                </div>
                <div css={userPriceStyle}>
                  {perUnit} {perUserTypeLabel}
                </div>
              </div>
            ) : (
              <div>
                <div css={monthlyPriceStyle}>
                  {formattedTotal} {annuallyBillingCycleLabel.toLowerCase()}
                </div>
                <div css={userPriceStyle}>
                  {userTierLabel} {unitLimitLower} - {unitLimitUpper}
                </div>
              </div>
            ))}
          <RemoveButton
            text={removeLabel}
            onClick={remove}
            appName={app.name}
          />
        </div>
      </div>
    </div>
  );
};
export default AppCard;
